import { ProjectService } from './project.service'
import { Topic } from './topic'
import { SearchTerm } from './search-term'
import { Tag } from './tag'
import { Note } from './note'
import { Criterion } from './criterion'
import { BatchSearch } from './batch-search'
import { DbRecord } from './db-record'

export class Project {
  private _title: string
  private _shortcut: string
  isNew: boolean;
  isInitialized = false;
  isInitStarted  = false;

  topicsMap = new Map<string, Topic>();
  searchTermsMap = new Map<string, SearchTerm>();
  tagsMap = new Map<string, Tag>();
  notesMap = new Map<string, Note>();
  criterionsMap = new Map<string, Criterion>();
  batchSearchMap = new Map<string, BatchSearch>();
  topicsSearchTermsMap = new Map<string, Set<string>>();
  dbRecordTopicsMap = new Map<string, Set<string>>();
  topicDbRecordsMap = new Map<string, Set<string>>();
  hasPatents = false;

  constructor(
    private projects: ProjectService,
    public id: string,
    title: string) {
    this.title = title;
    this.isNew = title === '';
    projects.projectMap.set(this.id, this);
  }

  getTopicDbRecords(topic: Topic) {
    let ls = [];
    let s = this.topicDbRecordsMap.get(topic.id);
    if (s) {
      s.forEach(x => ls.push(this.projects.getDbRecord(x)));
    }
    return ls.filter(x => !!x);
  }


  getDbRecordTopics(dbRecord: DbRecord) {
    let ls = [];
    this.getDbRecordTopicsIdSet(dbRecord).forEach(x =>
      ls.push(this.topicsMap.get(x))
    );
    ls.sort((a, b) => a.title.toLowerCase() < b.title.toLowerCase() ? -1 : 1);
    return ls;
  }

  getDbRecordTopicsIdSet(dbRecord: DbRecord) {
    return this.dbRecordTopicsMap.get(dbRecord.id) || new Set<string>();
  }

  private _setDbRecordTopicFromIds(dbRecordId, topicId) {
    let tSet = this.dbRecordTopicsMap.get(dbRecordId);
    let dbrSet = this.topicDbRecordsMap.get(topicId);

    if (!tSet) {
      tSet = new Set<string>();
      this.dbRecordTopicsMap.set(dbRecordId, tSet);
    }
    tSet.add(topicId);

    if (!dbrSet) {
      dbrSet = new Set<string>();
      this.topicDbRecordsMap.set(topicId, dbrSet);
    }
    dbrSet.add(dbRecordId);
  }

  setDbRecordTopic(dbRecord: DbRecord, topic: Topic) {
    this.projects.saveDbRecordTopic(this, dbRecord.id, topic.id)
      .subscribe(x => this._setDbRecordTopicFromIds(dbRecord.id, topic.id));
  }

  deleteDbRecordTopic(dbRecord: DbRecord, topic: Topic) {
    this.projects.deleteDbRecordTopic(this, dbRecord.id, topic.id)
      .subscribe(x => {
        this.dbRecordTopicsMap.get(dbRecord.id).delete(topic.id);
        this.topicDbRecordsMap.get(topic.id).delete(dbRecord.id);
      });
  }

  getNote(parentId: string) {
    return this.notesMap.get(parentId);
  }

  getTopicDbRecordNote(dbRecordId: string, topicId: string) {
    return this.notesMap.get(`${dbRecordId}%%${topicId}`);
  }

  set title(txt) {
    if (!txt) {
      this._title = '???';
    } else {
      this._title = txt.trim();
    }
  }

  get title() {
    return this._title;
  }

  set shortcut(txt) {
    this._shortcut = txt.trim();
  }

  get shortcut() {
    return this._shortcut;
  }

  initTopicDbRecords(obj) {
    obj = obj || {};
    let notesLs = [];
    Object.keys(obj).forEach(topicId => {
      let dbRecordDct = obj[topicId];
      Object.keys(dbRecordDct).forEach(dbRecordId => {
        this._setDbRecordTopicFromIds(dbRecordId, topicId);
        let note = dbRecordDct[dbRecordId];
        if (note[0])
          notesLs.push([topicId, dbRecordId, note[0], new Date(note[1])]);
      });
    });
    this.projects.initDbRecordTopicNotes(this, notesLs);
  }

  initProject() {
    if (this.isInitStarted) {
      return;
    }
    this.isInitStarted = true;
    this.projects.initProject(this);
  }

  get topicsList() {
    if (!this.isInitialized) {
      return [];
    }
    let ls = Array.from(this.topicsMap.values()).filter((x) => !x.isNew);
    return ls.sort((a, b) => a.title.toLowerCase() < b.title.toLowerCase() ? -1 : 1);
  }

  get searchTermsList() {
    if (!this.isInitialized)
      return [];
    let ls = Array.from(this.searchTermsMap.values());
    return ls.sort((a, b) => a.text.toLowerCase() < b.text.toLowerCase() ? -1 : 1);
  }

  get tagsList() {
    if (!this.isInitialized)
      return [];
    let ls = Array.from(this.tagsMap.values()).filter((x) => !x.isNew);
    return ls.sort((a, b) => a.title.toLowerCase() < b.title.toLowerCase() ? -1 : 1);
  }

  get criterionsList() {
    if (!this.isInitialized)
      return [];
    let ls = Array.from(this.criterionsMap.values()).filter((x) => !x.isNew);
    return ls.sort((a, b) => a.title.toLowerCase() < b.title.toLowerCase() ? -1 : 1);
  }

  setCurrentBatchSearch(bsid: string) {
    this.batchSearchMap.forEach(x =>
      x.isCurrentSearch = x.id === bsid
    );
  }

  get backSearchesList() {
    if (!this.isInitialized)
      return [];

    let ls = Array.from(this.batchSearchMap.values());
    return ls.sort((a, b) => a.date < b.date ? -1 : 1);
  }

  getTopic(tid: string) {
    return this.topicsMap.get(tid);
  }

  getSearchTerm(stid: string) {
    return this.searchTermsMap.get(stid);
  }

  topicSearchTerms(topic: Topic) {
    let ls = [];
    let sss = this.topicsSearchTermsMap.get(topic.id);
    if (!sss)
      return [];
    sss.forEach((x) => ls.push(this.getSearchTerm(x)));
    return ls.filter((x) => x);
  }

  searchTermTopics(searchTerm: SearchTerm) {
    let ls = [];
    let sss = this.topicsSearchTermsMap.get(searchTerm.id);
    if (!sss)
      return [];
    sss.forEach((x) => ls.push(this.getTopic(x)));
    return ls.filter((x) => x);
  }

  bindTopicSearchTerm(topic: Topic, searchTerm: SearchTerm) {
    let tSet = this.topicsSearchTermsMap.get(topic.id);
    let stSet = this.topicsSearchTermsMap.get(searchTerm.id);

    if (!tSet) {
      tSet = new Set<string>();
      this.topicsSearchTermsMap.set(topic.id, tSet);
    }

    if (!stSet) {
      stSet = new Set<string>();
      this.topicsSearchTermsMap.set(searchTerm.id, stSet);
    }

    if (!tSet.delete(searchTerm.id))
      tSet.add(searchTerm.id);

    if (!stSet.delete(topic.id))
      stSet.add(topic.id);

    this.projects.saveTopicSearchTermBinding(this);

  }

  isBound(topic: Topic, searchTerm: SearchTerm) {
    let s = this.topicsSearchTermsMap.get(topic.id);
    if (!s)
      return false;
    return s.has(searchTerm.id);
  }
}



// WEBPACK FOOTER //
// ./src/app/project.ts